import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldHalf, Plus, Trash2, Loader2, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const card = "border border-white/10 bg-black/40 p-5 flex flex-col gap-4";

const STATUS_CLS = { invited: "text-amber-400 border-amber-500/40", accepted: "text-nc-green border-nc-green/40", revoked: "text-zinc-500 border-white/15" };

/* Staff & Roles — owners define custom roles with granular hub-console permissions,
   then invite players by hub email or Steam profile. Invites land in the player's
   Citizen Hub for one-click accept. */
export function StaffPanel({ sid }) {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [roleForm, setRoleForm] = useState({ name: "", color: "#2E6BFF", perms: [] });
  const [invite, setInvite] = useState({ account: "", role_id: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/nc/servers/${sid}/roles`).then((r) => { setRoles(r.data.roles); setPerms(r.data.permissions); }).catch(() => {});
    api.get(`/nc/servers/${sid}/staff`).then((r) => setStaff(r.data.staff)).catch(() => {});
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const togglePerm = (p) => setRoleForm((f) => ({ ...f, perms: f.perms.includes(p) ? f.perms.filter((x) => x !== p) : [...f.perms, p] }));

  const createRole = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/roles`, roleForm);
      toast.success(r.data.message);
      setRoleForm({ name: "", color: "#2E6BFF", perms: [] });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const deleteRole = async (id) => {
    try {
      const r = await api.delete(`/nc/servers/${sid}/roles/${id}`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendInvite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/staff/invite`, invite);
      toast.success(r.data.message);
      setInvite({ account: "", role_id: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try {
      const r = await api.post(`/nc/servers/${sid}/staff/${id}/revoke`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* roles */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><ShieldHalf className="w-3.5 h-3.5" /> Custom roles</p>
        {roles.length === 0 && <p className="font-mono text-[10px] text-zinc-500">No roles yet — create one like "Head Moderator", then invite staff into it.</p>}
        {roles.map((r) => (
          <div key={r.id} data-testid={`role-row-${r.id.slice(0, 8)}`} className="border border-white/10 bg-[#070707] px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="w-3 h-3 shrink-0" style={{ backgroundColor: r.color }} />
            <span className="font-sans font-semibold text-sm">{r.name}</span>
            <span className="font-mono text-[8px] text-zinc-500 uppercase">{(r.perms || []).join(" · ") || "no permissions"}</span>
            <button data-testid={`role-del-${r.id.slice(0, 8)}`} onClick={() => deleteRole(r.id)} className="ml-auto text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div><span className={labelCls}>Role name</span><input data-testid="role-name" className={inputCls} maxLength={40} value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="Moderator" /></div>
            <div><span className={labelCls}>Color</span><input data-testid="role-color" className={inputCls} maxLength={9} value={roleForm.color} onChange={(e) => setRoleForm({ ...roleForm, color: e.target.value })} /></div>
          </div>
          <div>
            <span className={labelCls}>Console permissions</span>
            <div className="flex flex-wrap gap-2">
              {perms.map((p) => (
                <button key={p} data-testid={`role-perm-${p}`} onClick={() => togglePerm(p)}
                  className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1.5 border transition-colors ${roleForm.perms.includes(p) ? "border-nc-orange text-nc-orange bg-nc-orange/5" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="role-create" disabled={busy || roleForm.name.trim().length < 2} onClick={createRole} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors w-fit flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create role
          </button>
        </div>
      </div>

      {/* staff invites */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Staff team</p>
        {staff.length === 0 && <p className="font-mono text-[10px] text-zinc-500">Nobody invited yet — invite your first moderator below.</p>}
        {staff.map((m) => (
          <div key={m.id} data-testid={`staff-row-${m.id.slice(0, 8)}`} className="border border-white/10 bg-[#070707] px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[10px] text-zinc-200 break-all">{m.account}</span>
            <span className="font-mono text-[8px] uppercase border border-white/15 text-zinc-400 px-1.5 py-0.5">{m.role_name}</span>
            <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[m.status] || STATUS_CLS.invited}`}>{m.status}</span>
            <span className="font-mono text-[8px] text-zinc-600 uppercase">via {m.via}</span>
            <button data-testid={`staff-revoke-${m.id.slice(0, 8)}`} onClick={() => revoke(m.id)} className="ml-auto font-mono text-[8px] uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 px-2 py-1.5 transition-colors">Revoke</button>
          </div>
        ))}
        <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
          <div><span className={labelCls}>Hub email or Steam profile URL</span><input data-testid="invite-account" className={inputCls} maxLength={120} value={invite.account} onChange={(e) => setInvite({ ...invite, account: e.target.value })} placeholder="mod@yourserver.gg or https://steamcommunity.com/id/…" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className={labelCls}>Role</span>
              <select data-testid="invite-role" className={inputCls} value={invite.role_id} onChange={(e) => setInvite({ ...invite, role_id: e.target.value })}>
                <option value="">Select a role…</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div><span className={labelCls}>Note (optional)</span><input data-testid="invite-note" className={inputCls} maxLength={200} value={invite.note} onChange={(e) => setInvite({ ...invite, note: e.target.value })} placeholder="Weekend coverage" /></div>
          </div>
          <button data-testid="invite-send" disabled={busy || invite.account.trim().length < 3 || !invite.role_id} onClick={sendInvite} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors w-fit flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Send invite
          </button>
          <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">They accept from their Citizen Hub (and get an email if they already have an account). Accepted staff unlock this server's admin console in-game.</p>
        </div>
      </div>
    </div>
  );
}
